/* ============================================================
   PARTNER STUDIO — hasil ujian dipapar sebagai CARD Workspace
   ============================================================
   Arahan user 29 Okt: "pipeline kene bg output ke card sama mcm dlm workspace… display apa yg betul
   sama mcm kat backend dlm workspace sebenar, supaya bila module approve backend terima 100% smooth".

   SATU sumber dengan Workspace (tiada salinan):
     · susunHasilGenerate  — hasil server → prompts card (fungsi yang SAMA dipanggil generateForProduct)
     · masalahPromptKad + ringkasMasalahPrompt — semakan "sedia untuk Flow" yang SAMA sebelum Run
   Format module (1 / 2 scene) → kunci robot yang approve akan guna (ai8saat / ai16saat), jadi semakan
   card = semakan yang pelanggan akan kena.

   ⚠️ SEMENTARA (29 Okt): hasil makmal (labProcessSets) belum = pipeline Generate sebenar — sesi lain
   sedang membina Generate Resipi; selepas itu lab disambung ke pipeline yang sama (PLAN_PARTNER.md).
   `hasilKeServer` di bawah menukar bentuk makmal → bentuk jawapan server Generate. Bila lab sudah guna
   pipeline sebenar, fungsi itu dibuang dan jawapan server terus masuk susunHasilGenerate.
   ============================================================ */
import { useMemo, useState } from 'react';
import { Copy, Check, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Badge } from '../design/ui';
import { susunHasilGenerate } from '../utils/susunHasilGenerate';
import { masalahPromptKad, ringkasMasalahPrompt } from '../utils/semakPromptFlow';

const SET_KAD = ['set1', 'set2', 'set3'];
/** Format module → kunci robot yang module rasmi akan guna (ENGINE_IKUT_FORMAT di server). */
const skripIkutFormat = (format) => (Number(format) === 2 ? 'ai16saat' : 'ai8saat');

/** Bentuk set makmal → bentuk jawapan server Generate (set1..set3). Sementara — lihat kepala fail. */
function hasilKeServer(sets) {
    const keluar = {};
    (Array.isArray(sets) ? sets : []).slice(0, 3).forEach((s, i) => {
        const o = {
            promptImage: s.promptImage || '',
            promptVideo: s.promptVideo || '',
            videoScript: s.videoScript || '',
            captionTiktok: s.captionTiktok || '',
            hookText: s.hookText || '',
        };
        (s.scenes || []).forEach((sc) => {
            o[`promptVideoScene${sc.scene}`] = sc.promptVideo || '';
            o[`videoScript${sc.scene}`] = sc.videoScript || '';
        });
        keluar[`set${i + 1}`] = o;
    });
    return keluar;
}

/** Hasil ujian → card (prompts) dengan kod Workspace. Module Partner tiada ayat wajib → logic kosong. */
function kadDariHasil(sets) {
    const { newPrompts } = susunHasilGenerate({
        current: { prompts: {} },
        combinedResult: hasilKeServer(sets),
        logic: {},
        appendProduct: null,
    });
    return { prompts: newPrompts };
}

const T_INGGERIS = (kunci, lalai, vars) => {
    const peta = {
        'ws.flowPrompt.kurangDua': 'Image Prompt and Video Prompt',
        'ws.flowPrompt.kurangGambar': 'Image Prompt',
        'ws.flowPrompt.kurangVideo': 'Video Prompt',
    };
    if (peta[kunci]) return peta[kunci];
    if (kunci === 'ws.flowPrompt.barisSet') return `Set ${vars.n}: no ${vars.apa}`;
    if (kunci === 'ws.flowPrompt.barisBabak') return `Scene ${vars.n}: no prompt`;
    return lalai;
};

function SalinIkon({ teks }) {
    const [ok, setOk] = useState(false);
    if (!teks) return null;
    return (
        <button type="button" className="s-iconbtn" aria-label="Copy" title="Copy" style={{ width: 22, height: 22, flex: 'none' }}
            onClick={() => navigator.clipboard?.writeText(teks).then(() => { setOk(true); setTimeout(() => setOk(false), 1200); }).catch(() => {})}>
            {ok ? <Check size={12} /> : <Copy size={12} />}
        </button>
    );
}

function Petak({ teks, mono }) {
    return (
        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--s-line)', minWidth: 0, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            {teks ? (
                <div style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.5, maxHeight: 150, overflowY: 'auto', fontFamily: mono ? 'var(--s-mono)' : undefined, color: mono ? 'var(--s-ink-2)' : undefined }}>{teks}</div>
            ) : (
                <div style={{ flex: 1, fontSize: 12, color: 'var(--s-warn)' }}>empty</div>
            )}
            <SalinIkon teks={teks} />
        </div>
    );
}

/**
 * Card hasil ujian — rupa matriks "Edit Prompts" Workspace (Set 1–3 sebagai lajur).
 * @param sets    set hasil makmal (labJalan / adminUji / larianBaca)
 * @param format  1 | 2 (scene video module)
 */
export default function PartnerKadHasil({ sets, format }) {
    const f = Number(format) === 2 ? 2 : 1;
    const kad = useMemo(() => kadDariHasil(sets), [sets]);
    const masalah = useMemo(() => masalahPromptKad(kad, { scriptKey: skripIkutFormat(f), sets: SET_KAD, sceneCount: f }), [kad, f]);
    const ringkas = ringkasMasalahPrompt(masalah, T_INGGERIS);
    const p = (s) => kad.prompts[s] || {};
    // Baris = medan card yang robot baca (Workspace AI8SAAT · AI16SAAT).
    const baris = f === 2
        ? [
            { label: 'Hook', ambil: (x) => x.hookText },
            { label: 'Image prompt', ambil: (x) => x.promptImage, mono: true },
            { label: 'Script · scene 1', ambil: (x) => x.videoScript1 },
            { label: 'Video prompt · scene 1', ambil: (x) => x.promptVideo1 || x.promptVideo, mono: true },
            { label: 'Script · scene 2', ambil: (x) => x.videoScript2 },
            { label: 'Video prompt · scene 2', ambil: (x) => x.promptVideo2, mono: true },
            { label: 'Caption', ambil: (x) => x.captionTiktok },
        ]
        : [
            { label: 'Hook', ambil: (x) => x.hookText },
            { label: 'Image prompt', ambil: (x) => x.promptImage, mono: true },
            { label: 'Script', ambil: (x) => x.videoScript },
            { label: 'Video prompt', ambil: (x) => x.promptVideo, mono: true },
            { label: 'Caption', ambil: (x) => x.captionTiktok },
        ];
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 10, marginBottom: 10, border: `1px solid ${masalah.length ? 'var(--s-warn)' : 'var(--s-line)'}` }}>
                {masalah.length
                    ? <AlertTriangle size={15} style={{ color: 'var(--s-warn)', flex: 'none', marginTop: 1 }} aria-hidden="true" />
                    : <CheckCircle2 size={15} style={{ color: 'var(--s-ok)', flex: 'none', marginTop: 1 }} aria-hidden="true" />}
                <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                    {masalah.length ? (
                        <>
                            <strong>This card wouldn't run in Flow.</strong> {ringkas.baris}.
                            {/* Bug 1 (1 Nov): dahulu "fix your prompt's OUTPUT section" — tetapi SESAAT yang pegang bahagian
                                format (blokFormatOutput), Partner tak menulisnya. Suruh perkara yang Partner BOLEH ubah. */}
                            <span className="s-dim"> Flow checks the same thing before every run in Workspace. Make sure your prompt covers every part of each set ({f === 2 ? 'hook, image, script and video for both scenes, caption' : 'hook, image, script, video, caption'}). SESAAT handles the field names.</span>
                        </>
                    ) : (
                        <><strong>Ready for Flow.</strong> <span className="s-dim">This is the card a customer gets ({f} scene{f > 1 ? 's' : ''}).</span></>
                    )}
                </div>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--s-line)', borderRadius: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '110px repeat(3, minmax(180px, 1fr))', minWidth: 650 }}>
                    <div />
                    {SET_KAD.map((s, i) => {
                        const kurang = masalah.find((m) => m.set === s);
                        return (
                            <div key={s} style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <strong style={{ fontSize: 12.5 }}>Set {i + 1}</strong>
                                {kurang ? <Badge tone="warn">incomplete</Badge> : null}
                            </div>
                        );
                    })}
                    {baris.map((b) => (
                        <div key={b.label} style={{ display: 'contents' }}>
                            <div className="s-dim" style={{ padding: '8px 10px', borderTop: '1px solid var(--s-line)', fontSize: 12 }}>{b.label}</div>
                            {SET_KAD.map((s) => <Petak key={s} teks={b.ambil(p(s)) || ''} mono={b.mono} />)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
